import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 10;

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isGift = params.get("gift") === "1";
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [giftCode, setGiftCode] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let timer;
    const check = async () => {
      attempts.current += 1;
      try {
        const url = isGift ? `/gifts/status/${sessionId}` : `/payments/status/${sessionId}`;
        const { data } = await api.get(url);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (isGift) setGiftCode(data.code);
          if (!isGift) await refresh();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("error");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timer = setTimeout(check, POLL_INTERVAL);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("error");
        } else {
          timer = setTimeout(check, POLL_INTERVAL);
        }
      }
    };
    check();
    return () => clearTimeout(timer);
  }, [sessionId, refresh, isGift]);

  return (
    <div className="min-h-screen grid place-items-center p-6" data-testid="payment-success">
      <div className="soft-card p-8 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-[#23375E]" />
            <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Confirmation du paiement...</h2>
            <p className="mt-2 text-[#77849C]">Un instant, Stripe finalise ta commande.</p>
          </>
        )}
        {status === "paid" && !isGift && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#23375E]" />
            <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Bienvenue dans Kynoria 🎉</h2>
            <p className="mt-2 text-[#44506B]">Ton abonnement est actif. Tout est débloqué.</p>
            <Link
              to="/"
              className="mt-6 inline-block brand-btn rounded-full px-6 py-3 font-semibold"
              data-testid="go-dashboard-btn"
            >
              Aller au tableau de bord
            </Link>
          </>
        )}
        {status === "paid" && isGift && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#E8899F]" />
            <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Cadeau prêt 🎁</h2>
            <p className="mt-2 text-[#44506B]">Voici le code à partager avec ton destinataire :</p>
            {giftCode && (
              <div className="mt-4 rounded-2xl bg-[#FFF4DC] p-4 font-mono text-xl font-bold text-[#16233D] tracking-widest" data-testid="gift-code-display">
                {giftCode}
              </div>
            )}
            <Link
              to="/cadeaux"
              className="mt-6 inline-block brand-btn rounded-full px-6 py-3 font-semibold"
              data-testid="go-gifts-btn"
            >
              Voir tous mes cadeaux
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-500" />
            <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Paiement non finalisé</h2>
            <p className="mt-2 text-[#44506B]">Aucun montant n'a été débité.</p>
            <Link to="/abonnement" className="mt-6 inline-block brand-btn rounded-full px-6 py-3">
              Réessayer
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-[#E8899F]" />
            <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Confirmation en cours</h2>
            <p className="mt-2 text-[#44506B]">
              Le paiement met plus longtemps que prévu. Ton achat sera activé dès validation.
            </p>
            <Link to="/" className="mt-6 inline-block brand-btn rounded-full px-6 py-3">
              Continuer
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancelPage() {
  return (
    <div className="min-h-screen grid place-items-center p-6" data-testid="payment-cancel">
      <div className="soft-card p-8 max-w-md w-full text-center">
        <XCircle className="mx-auto h-14 w-14 text-[#77849C]" />
        <h2 className="mt-4 text-2xl font-bold text-[#16233D]">Paiement annulé</h2>
        <p className="mt-2 text-[#44506B]">Tu peux reprendre à tout moment.</p>
        <Link
          to="/abonnement"
          className="mt-6 inline-block brand-btn rounded-full px-6 py-3 font-semibold"
        >
          Voir les formules
        </Link>
      </div>
    </div>
  );
}
